import { useEffect, useId, useState } from 'react';
import {
  QUALIFICATION_LABELS,
  suggestDealName,
  type LeadCreationField,
  type SyncOp,
} from '@wr/shared';
import { get } from '../../api.js';
import { importHubSpotContact } from './data.js';
import {
  Field,
  ManyOfChips,
  Notice,
  OneOfChips,
  RadioChips,
  SelectField,
  TextAreaField,
} from '../../components/ui.js';
import { LISTS, marketLabel, validateLeadCreation } from '../../lists.js';
import { useOffline } from '../../offline/context.js';
import { newId } from '../../offline/ids.js';
import { useSignedIn } from '../../session.js';
import type { Stage } from './data.js';
import { stageFor } from './stages.js';

/**
 * Block 2, the project (the lead), the same everywhere (decision of Nov 2,
 * 2026, New Clients qualification flow): the four answers (role of this
 * person in the project, applications, installation country, pain), then
 * the Discovery Capture, folded: the product fit and its two outcomes,
 * Looks qualified (the Project Call date, or the task Book the Project
 * Call) or Not qualified (why and when: Nurture). One Save: a filled
 * Discovery Capture makes the lead complete; what is missing goes to To do.
 */
export interface ProjectDraft {
  role: string;
  applications: string[];
  installation_country: string;
  pain: string;
  product_fit: string;
  qualification: string;
  project_call_date: string;
  disqualified_why: string;
  disqualified_when: string;
}

export type ProjectErrors = Partial<Record<LeadCreationField, string>>;

const str = (v: unknown) => (typeof v === 'string' ? v : '');

/** A new project, prefilled from what the person already has (their country for the installation). */
export function projectFrom(person: Readonly<Record<string, unknown>> = {}): ProjectDraft {
  return {
    role: str(person.role),
    applications: Array.isArray(person.applications) ? (person.applications as string[]) : [],
    installation_country: str(person.installation_country) || str(person.country),
    pain: str(person.pain),
    product_fit: str(person.product_fit),
    qualification: str(person.qualification),
    project_call_date: str(person.project_call_date).slice(0, 10),
    disqualified_why: str(person.disqualified_why),
    disqualified_when: str(person.disqualified_when),
  };
}

/** The four answers checked; null when they are all given. */
export function projectErrors(draft: ProjectDraft): ProjectErrors | null {
  const result = validateLeadCreation(draft);
  if (result.ok) return null;
  return Object.fromEntries(result.errors.map((e) => [e.field, e.message]));
}

/** The Discovery Capture fields the person filled (only for those allowed to qualify). */
export function discoveryFields(
  draft: ProjectDraft,
  stages: Stage[],
): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  if (draft.product_fit.trim()) out.product_fit = draft.product_fit.trim();
  if (draft.qualification === 'qualified' || draft.qualification === 'disqualified') {
    out.qualification = draft.qualification;
    const stage = stageFor(draft.qualification, stages);
    if (stage) out.stage_id = stage;
    if (draft.qualification === 'qualified') {
      if (draft.project_call_date) out.project_call_date = draft.project_call_date;
    } else {
      if (draft.disqualified_why.trim()) out.disqualified_why = draft.disqualified_why.trim();
      if (draft.disqualified_when.trim()) out.disqualified_when = draft.disqualified_when.trim();
    }
  }
  return out;
}

/** The person a new lead is created for: an existing contact, or one created in the same save. */
export interface LeadPerson {
  id: string;
  company: string | null;
  country: string | null;
  event_id: string | null;
  /** The answers the contact had, to save on it what it was missing (brief section 6.7). */
  had?: Readonly<Record<string, unknown>>;
  /** False when the person may not change this contact. */
  canChange?: boolean;
}

/**
 * The changes that create the lead of a person: the lead (the four answers,
 * company, deal name and market suggested as Lead Entry, owner, the
 * Discovery Capture filled), its link to the person (primary), and on the
 * contact the answers it did not have yet.
 */
export function leadOps({
  person,
  draft,
  eventId,
  eventCode,
  ownerId,
  qualify,
  stages,
  changedAt = new Date().toISOString(),
}: {
  person: LeadPerson;
  draft: ProjectDraft;
  eventId: string | null;
  eventCode: string | null;
  ownerId: string;
  qualify: boolean;
  stages: Stage[];
  changedAt?: string;
}): { leadId: string; ops: SyncOp[] } {
  const result = validateLeadCreation(draft);
  if (!result.ok) throw new Error('The four answers are missing.');
  const answers = result.value;
  const ops: SyncOp[] = [];
  if (person.had && person.canChange !== false) {
    const had = person.had;
    const missing: Record<string, unknown> = {};
    if (!str(had.role)) missing.role = answers.role;
    if (!str(had.pain)) missing.pain = answers.pain;
    if (!(Array.isArray(had.applications) && had.applications.length > 0)) {
      missing.applications = answers.applications;
    }
    if (Object.keys(missing).length > 0) {
      ops.push({
        opId: newId(),
        entity: 'contact',
        id: person.id,
        action: 'upsert',
        changedAt,
        fields: missing,
      });
    }
  }
  const leadId = newId();
  ops.push(
    {
      opId: newId(),
      entity: 'lead',
      id: leadId,
      action: 'upsert',
      changedAt,
      fields: {
        ...answers,
        // The company's country: the person's, else where the system goes.
        country: person.country || answers.installation_country,
        company_name: person.company || null,
        event_id: eventId,
        deal_name: suggestDealName(person.company, eventCode) || null,
        deal_market: answers.applications[0],
        owner_user_id: ownerId,
        ...(qualify ? discoveryFields(draft, stages) : {}),
      },
    },
    {
      opId: newId(),
      entity: 'lead_contact',
      action: 'link',
      leadId,
      contactId: person.id,
      isPrimary: true,
      position: 0,
      changedAt,
    },
  );
  return { leadId, ops };
}

const OUTCOMES = [
  { value: 'qualified', label: QUALIFICATION_LABELS.qualified },
  { value: 'disqualified', label: QUALIFICATION_LABELS.disqualified },
];

/** Block 2 on screen: the four answers, then the Discovery Capture, folded. */
export function ProjectBlock({
  value,
  onChange,
  errors,
  personName,
  discoveryOpen,
  onDiscoveryToggle,
}: {
  value: ProjectDraft;
  onChange(value: ProjectDraft): void;
  errors: ProjectErrors;
  /** "this person" when the name is not known yet. */
  personName?: string;
  discoveryOpen?: boolean;
  onDiscoveryToggle?(open: boolean): void;
}) {
  const { can } = useSignedIn();
  const prefix = useId();
  const qualify = can('leads.qualify');
  const [openSelf, setOpenSelf] = useState(() => Boolean(value.product_fit || value.qualification));
  const open = discoveryOpen ?? openSelf;
  const set = <K extends keyof ProjectDraft>(field: K, v: ProjectDraft[K]) =>
    onChange({ ...value, [field]: v });
  const who = personName?.trim() || 'this person';

  return (
    <div className="formsec" role="group" aria-label="Project">
      <h3>Project</h3>
      <span className="note" style={{ marginTop: -10 }}>
        The lead: four answers, all required
      </span>
      <RadioChips
        id={`${prefix}-role`}
        label={`Role of ${who} in the project`}
        mark
        options={LISTS.roleOptions}
        value={value.role}
        error={errors.role}
        onChange={(v) => set('role', v)}
      />
      <ManyOfChips
        id={`${prefix}-applications`}
        label="Applications"
        mark
        options={LISTS.markets.map((m) => ({ value: m.code, label: marketLabel(m.code) }))}
        value={value.applications}
        error={errors.applications}
        onChange={(v) => set('applications', v)}
      />
      <SelectField
        id={`${prefix}-installation_country`}
        label="Installation country"
        hint="The person's country, unless the system goes elsewhere"
        mark
        value={value.installation_country}
        error={errors.installation_country}
        onChange={(e) => set('installation_country', e.target.value)}
      >
        <option value="">Select a country</option>
        {LISTS.countries.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </SelectField>
      <Field
        id={`${prefix}-pain`}
        label="Pain"
        hint="In the client's words: what they sort, the most frustrating part"
        mark
        value={value.pain}
        error={errors.pain}
        onChange={(e) => set('pain', e.target.value)}
      />
      <details
        className="k-more"
        open={open}
        onToggle={(e) => {
          const now = (e.currentTarget as HTMLDetailsElement).open;
          if (onDiscoveryToggle) onDiscoveryToggle(now);
          else setOpenSelf(now);
        }}
      >
        <summary>Discovery Capture (now, or later from To do)</summary>
        <div className="k-more-body">
          {qualify ? (
            <>
              <TextAreaField
                label="Product fit"
                hint="Technical feasibility, from our expertise: material, throughput, presentation on the belt"
                rows={3}
                value={value.product_fit}
                onChange={(e) => set('product_fit', e.target.value)}
              />
              <OneOfChips
                label="Outcome"
                options={OUTCOMES}
                value={value.qualification}
                onChange={(v) => set('qualification', v)}
              />
              {value.qualification === 'qualified' && (
                <Field
                  label="Project Call"
                  hint="No date yet: a task Book the Project Call goes to HubSpot"
                  type="date"
                  value={value.project_call_date}
                  onChange={(e) => set('project_call_date', e.target.value)}
                />
              )}
              {value.qualification === 'disqualified' && (
                <>
                  <Field
                    label="Why not qualified"
                    mark
                    value={value.disqualified_why}
                    onChange={(e) => set('disqualified_why', e.target.value)}
                  />
                  <Field
                    label="When this can change"
                    hint="The lead goes to Nurture"
                    mark
                    placeholder="e.g. new line planned in 2028"
                    value={value.disqualified_when}
                    onChange={(e) => set('disqualified_when', e.target.value)}
                  />
                </>
              )}
            </>
          ) : (
            <p className="note" style={{ margin: 0 }}>
              The Discovery Capture is completed by a person allowed to qualify leads.
            </p>
          )}
        </div>
      </details>
    </div>
  );
}

/** Moves the focus to the first answer at fault, after the render that shows it. */
export function focusFirst(root: HTMLElement | null, errors: ProjectErrors) {
  const first = (['role', 'applications', 'installation_country', 'pain'] as const).find(
    (f) => errors[f],
  );
  if (!first) return;
  setTimeout(() => root?.querySelector<HTMLElement>(`[id$="-${first}"]`)?.focus());
}

/** The contact the email already belongs to, in the portal or HubSpot (exact email). */
export interface Known {
  id: string | null;
  hubspotId?: string;
  label: string;
  company: string | null;
  source: 'portal' | 'hubspot';
}

/** "Already a contact" on the exact email (decision of Nov 3, 2026): never two contacts. */
export function useKnownContact(email: string): Known | null {
  const [found, setFound] = useState<{ q: string; contact: Known | null } | null>(null);
  const q = email.trim().toLowerCase();
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(q);
  const { store } = useOffline();
  useEffect(() => {
    if (!valid) return;
    let current = true;
    const timer = setTimeout(() => {
      void get<{ contact: Known | null }>(
        `/people/contacts/by-email?email=${encodeURIComponent(q)}`,
      )
        .catch(async () => {
          // Without network: this device's own contacts.
          const local = (await store?.listRecords('contact').catch(() => [])) ?? [];
          const hit = local.find(
            (c) => typeof c.email === 'string' && c.email.trim().toLowerCase() === q,
          );
          return {
            contact: hit
              ? {
                  id: hit.id,
                  label: [hit.first_name, hit.last_name].filter(Boolean).join(' ') || q,
                  company: typeof hit.company === 'string' ? hit.company : null,
                  source: 'portal' as const,
                }
              : null,
          };
        })
        .then((r) => current && setFound({ q, contact: r.contact }));
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [q, valid, store]);
  return valid && found?.q === q ? found.contact : null;
}

/** "Is there a project?" No: a contact. Yes: block 2 opens on the same screen. */
export function IsThereAProject({
  value,
  onChange,
}: {
  value: boolean;
  onChange(yes: boolean): void;
}) {
  return (
    <div className="fm" style={{ margin: 0 }}>
      <b>Is there a project?</b>
      <div className="chips" role="group" aria-label="Is there a project?">
        {[
          { yes: false, label: 'No, a contact' },
          { yes: true, label: 'Yes, a lead' },
        ].map((o) => (
          <button
            key={o.label}
            type="button"
            className={`le-chip${value === o.yes ? ' on' : ''}`}
            aria-pressed={value === o.yes}
            onClick={() => onChange(o.yes)}
          >
            {o.label}
          </button>
        ))}
      </div>
      <small className="note">
        {value
          ? 'A lead needs the four answers below. The Discovery Capture can wait for To do.'
          : 'Not sure yet? Save a contact, and create the lead later from its sheet.'}
      </small>
    </div>
  );
}

/** The portal ID of a known contact; one found in HubSpot is imported first (never twice). */
export async function knownContactId(known: Known): Promise<string | null> {
  if (known.id) return known.id;
  return known.hubspotId ? importHubSpotContact(known.hubspotId) : null;
}

/** Already a contact: open it, or add a lead to it; never a second contact. */
export function KnownContact({
  known,
  onOpen,
  what = 'This scan does not make a second one.',
}: {
  known: Known;
  onOpen(lead: boolean): void;
  what?: string;
}) {
  const { can } = useSignedIn();
  return (
    <Notice kind="warn">
      <b>Already a contact:</b> {known.label}
      {known.company ? `, ${known.company}` : ''}
      {known.source === 'hubspot' ? ' (in HubSpot)' : ''}. {what}
      <span className="actions" style={{ marginTop: 8 }}>
        <button type="button" className="btn sm primary" onClick={() => onOpen(false)}>
          Open this contact
        </button>
        {can('leads.create') && (
          <button type="button" className="btn sm" onClick={() => onOpen(true)}>
            Add a lead to it
          </button>
        )}
      </span>
    </Notice>
  );
}
